/**
 * deriveGlbTextures — ADR-020 §3's seam, applied to a finished GLB: classify
 * every embedded texture by the ROLE its materials give it, then rewrite the
 * file with each image derived channel-aware to the delivery ceiling and
 * with out-of-band roughness factors BAKED into the roughness channel so the
 * shipped file is spec-clean (glTF caps factors at 1; the wet-skin cure is
 * >1 by ratification §11 — export-real means the file carries the truth).
 *
 * Two independent engagements, deliberately:
 *  - RESIZE engages only when the file carries master-class textures (any
 *    image ≥ `masterClassMinDim`, default 4096 — the T0 intake class; Tripo
 *    ships albedo 8192², MR/normal 4096²). A delivery-sized file (the
 *    grandfathered roster, VRM imports) is never silently re-filtered.
 *  - FACTOR BAKE engages whenever a material's roughnessFactor > 1,
 *    whatever the texture size — a 1024-class character takes the wet-skin
 *    cure at native resolution.
 *
 * PNG **and JPEG** both derive (JPEG since 2026-09-01). Tripo's newer exports
 * ship JPEG, and while they were skipped the delivery ceiling did not bite them
 * AND a >1 roughness bake had nowhere to land — which refused a whole export in
 * the field. A derived image is always written back as PNG (lossless from that
 * point, and a JPEG re-encode would add a second generation of loss to maps
 * World compresses again downstream), with its mime type rewritten to match.
 *
 * Fail-loud (§7): an sRGB/linear role conflict, a >1 factor with no
 * roughness texture to bake into, a >1 metallic factor, a 16-bit or
 * palette PNG, a shared-factor contradiction, and a texture reached only
 * through an extension slot outside EXTENSION_TEXTURE_ROLES all REFUSE
 * naming the material/image; nothing ships half-derived.
 *
 * Depends on: fast-png, jpeg-js, glb-container, derive-core, errors.
 * Depended on by: the app's export door, the T2 probe.
 */
import { decode, encode } from 'fast-png';
import { decode as decodeJpeg } from 'jpeg-js';
import {
  buildGlb,
  imageBytes,
  parseGlbContainer,
  jpegDimensions,
  pngDimensions,
  type ImageReplacement,
  replaceImages,
  type GlbJson,
} from './glb-container.js';
import {
  fromPlanes,
  linearPlaneToSrgbBytes,
  renormalizeNormals,
  resizePlane,
  rethresholdMetallic,
  roundPlane,
  scalePlane,
  fillPlane,
  srgbPlaneToLinear,
  toPlanes,
} from './derive-core.js';
import { TextureMasterError } from './errors.js';

/**
 * The delivery ceiling for roles with no explicit entry in
 * {@link DELIVERY_ROLE_CEILINGS} (re-derivable — L3, never a fork). A ceiling
 * is a DERIVE parameter, never a property of the master: every delivery
 * re-derives from the master it is bound to, so changing one changes what we
 * ship next, not what we shipped.
 */
export const DELIVERY_TEXTURE_SIZE = 1024;

/**
 * PER-ROLE delivery ceilings (2026-09-01, from World's measurement of their
 * own KTX2 pass — Allen relaying it):
 *
 * World rewrites a delivered avatar in place by material role: **baseColor
 * and emissive are compressed to ETC1S; normal, metallicRoughness and
 * occlusion are left uncompressed**. So the two families have opposite
 * economics, and one uniform ceiling prices them wrong:
 *
 *  - **baseColor/emissive at 2048 is free.** ETC1S is ~4:1, so a 2048 map
 *    costs in VRAM what an uncompressed 1024 costs today — 4× the texel
 *    density for the same residency. That headroom is the point: the face and
 *    eyes are where a lossy pass shows first, and they occupy a small fraction
 *    of an auto-generated atlas (ADR-020 §1).
 *  - **normal/MR/occlusion stay 1024.** They are never compressed downstream,
 *    so doubling them is pure cost — 4× the bytes and 4× the residency for
 *    channels whose detail survives a KTX2 pass anyway.
 *
 * A role absent here falls back to {@link DELIVERY_TEXTURE_SIZE}.
 */
export const DELIVERY_ROLE_CEILINGS: Partial<Record<TextureRole, number>> = {
  albedo: 2048,
  color: 2048,
  emissive: 2048,
};

/**
 * The mastered-class gate: only T0-intake files carry textures this large
 * (Tripo caps MR/normal at 4096² even on the 8K export; albedo ships 8192²).
 */
export const MASTER_CLASS_MIN_DIM = 4096;

export type TextureRole = 'albedo' | 'color' | 'emissive' | 'linear' | 'mr' | 'normal' | 'occlusion' | 'unknown';

export interface DeriveOptions {
  /** Every image derives to fit within this (max dimension). */
  readonly maxSize?: number;
  /**
   * PER-ROLE ceilings, overriding {@link DeriveOptions.maxSize} for images
   * carrying that role (default {@link DELIVERY_ROLE_CEILINGS}). An image
   * serving SEVERAL roles takes the LARGEST of their ceilings: a map shared
   * between baseColor and occlusion must not lose the baseColor detail the
   * larger ceiling exists to protect — the cheaper role rides along at the
   * higher resolution rather than the expensive one being shrunk.
   */
  readonly roleMaxSize?: Partial<Record<TextureRole, number>>;
  /** Resize engages only when some image dimension reaches this. */
  readonly masterClassMinDim?: number;
  /**
   * Channels DETACHED from the metallicRoughness map, to be written into it as
   * constants.
   *
   * glTF reads both channels from one texture whenever it is present, so
   * "metallic from the map, roughness flat" is not expressible as a material
   * property — the surface can do it (three keeps two independent map slots)
   * and the file cannot. Baking the constant into that channel's plane is what
   * makes the exported file agree with what the viewport showed, leaving the
   * other channel's map intact beside it.
   *
   * `value` is the channel's own [0,1] value, not a byte.
   */
  readonly flatten?: readonly {
    readonly material: string;
    readonly channel: 'roughness' | 'metallic';
    readonly value: number;
  }[];
}

export interface DerivedImage {
  readonly index: number;
  readonly roles: readonly TextureRole[];
  readonly from: readonly [number, number];
  readonly to: readonly [number, number];
  readonly rule: string;
}

export interface DeriveGlbReport {
  /** False = the input IS the output: nothing was master-class, nothing baked. */
  readonly changed: boolean;
  readonly resizeEngaged: boolean;
  readonly images: readonly DerivedImage[];
  readonly bakedRoughness: readonly { material: string; factor: number }[];
  /** Images left untouched WITH the reason — a cap is never silent (LAWS). */
  readonly skipped: readonly { index: number; reason: string }[];
}

interface ImageFacts {
  readonly index: number;
  readonly roles: Set<TextureRole>;
  bakeFactor: number | null;
  /** Constant byte to write into G (roughness) / B (metallic), or null. */
  flattenG: number | null;
  flattenB: number | null;
}

const SRGB_ROLES: readonly TextureRole[] = ['albedo', 'emissive', 'color'];
const LINEAR_ROLES: readonly TextureRole[] = ['mr', 'normal', 'occlusion', 'linear'];

/**
 * Material-extension texture slots, classified by each extension spec's own
 * channel semantics: 'color' filters like baseColor (sRGB-linear Lanczos),
 * 'linear' like occlusion (plain), 'normal' renormalizes. Tripo's HD tier
 * reportedly ships specular-class extensions, so the specular rows are
 * production-bound, not archaeology. A texture reached through an extension
 * or slot NOT in this table REFUSES (§7) — never a silent plain-Lanczos
 * fallback over channels whose encoding we have not read in a spec.
 */
const EXTENSION_TEXTURE_ROLES: Readonly<Record<string, Readonly<Record<string, TextureRole>>>> = {
  KHR_materials_sheen: { sheenColorTexture: 'color', sheenRoughnessTexture: 'linear' },
  KHR_materials_specular: { specularColorTexture: 'color', specularTexture: 'linear' },
  KHR_materials_clearcoat: {
    clearcoatTexture: 'linear',
    clearcoatRoughnessTexture: 'linear',
    clearcoatNormalTexture: 'normal',
  },
  KHR_materials_transmission: { transmissionTexture: 'linear' },
  KHR_materials_iridescence: { iridescenceTexture: 'linear', iridescenceThicknessTexture: 'linear' },
  KHR_materials_emissive_strength: {}, // carries no textures — known, nothing to classify
  KHR_materials_anisotropy: { anisotropyTexture: 'linear' },
};

/** The textureInfo shape — how a texture reference is recognized inside an extension object. */
function textureIndexOf(value: unknown): number | undefined {
  if (typeof value === 'object' && value !== null && typeof (value as { index?: unknown }).index === 'number') {
    return (value as { index: number }).index;
  }
  return undefined;
}

/** texture index → image index, or null when a plugin owns the source. */
function textureImage(json: GlbJson, textureIndex: number): number | null {
  const texture = json.textures?.[textureIndex];
  return texture?.source ?? null;
}

export function deriveGlbTextures(
  glb: ArrayBuffer,
  options: DeriveOptions = {},
): { glb: ArrayBuffer; report: DeriveGlbReport } {
  const maxSize = options.maxSize ?? DELIVERY_TEXTURE_SIZE;
  // An explicit flat maxSize with no explicit per-role map means the caller
  // asked for ONE size (the T1 prototype cross-check, the synthetic tests):
  // honour it rather than letting the shipping role ceilings override it.
  const roleMaxSize = options.roleMaxSize ?? (options.maxSize === undefined ? DELIVERY_ROLE_CEILINGS : {});
  const masterClassMinDim = options.masterClassMinDim ?? MASTER_CLASS_MIN_DIM;
  const container = parseGlbContainer(glb);
  const { json } = container;
  const images = json.images ?? [];
  const materials = json.materials ?? [];

  // ---- classify every image by the roles its materials give it ----
  const facts = new Map<number, ImageFacts>();
  const factsFor = (imageIndex: number): ImageFacts => {
    const hit = facts.get(imageIndex);
    if (hit !== undefined) return hit;
    const made: ImageFacts = { index: imageIndex, roles: new Set(), bakeFactor: null, flattenG: null, flattenB: null };
    facts.set(imageIndex, made);
    return made;
  };
  const issues: string[] = [];
  const flattenRequests = options.flatten ?? [];
  materials.forEach((material, m) => {
    const name = material.name ?? `#${m}`;
    const pbr = material.pbrMetallicRoughness;
    const roleOf = (textureIndex: number | undefined, role: TextureRole): number | null => {
      if (textureIndex === undefined) return null;
      const img = textureImage(json, textureIndex);
      if (img !== null) factsFor(img).roles.add(role);
      return img;
    };
    roleOf(pbr?.baseColorTexture?.index, 'albedo');
    roleOf(material.emissiveTexture?.index, 'emissive');
    const mrImage = roleOf(pbr?.metallicRoughnessTexture?.index, 'mr');
    roleOf(material.normalTexture?.index, 'normal');
    roleOf(material.occlusionTexture?.index, 'occlusion');

    // Extension slots classify from the table; a texture reached through an
    // unclassified extension/slot refuses — its encoding is unread, and a
    // plain-Lanczos guess is exactly the silent wrong §7 exists to forbid.
    for (const [extName, ext] of Object.entries(material.extensions ?? {})) {
      if (typeof ext !== 'object' || ext === null) continue;
      const slots = EXTENSION_TEXTURE_ROLES[extName];
      for (const [slot, value] of Object.entries(ext)) {
        const textureIndex = textureIndexOf(value);
        if (textureIndex === undefined) continue; // factors, scales, strengths — not textures
        const role = slots?.[slot];
        if (role === undefined) {
          issues.push(
            `material "${name}" references a texture through ${extName}.${slot}, a slot this derive has ` +
              `no channel semantics for — an unclassified texture cannot be filtered honestly; refused (§7)`,
          );
          continue;
        }
        roleOf(textureIndex, role);
      }
    }

    const metallic = pbr?.metallicFactor ?? 1;
    if (metallic > 1) {
      issues.push(
        `material "${name}" carries metallicFactor ${metallic} — beyond glTF's [0,1], and metal is ` +
          `semantically binary so there is no honest bake; refused (§7)`,
      );
    }
    // A detached channel becomes a constant in this image's plane. Two
    // materials sharing an image cannot want different constants — the same
    // law the roughness bake already keeps.
    for (const request of flattenRequests) {
      if (request.material !== name) continue;
      if (mrImage === null) {
        issues.push(
          `material "${name}" detaches ${request.channel} from its metallicRoughness map, but has no such ` +
            `texture to write the constant into — refused (§7)`,
        );
        continue;
      }
      const f = factsFor(mrImage);
      // The image is SHARED, so flattening it speaks for every material that
      // reads it — and the factor reset below lands on all of them too. A
      // material that never asked would silently ship a constant where its map
      // used to be (Codex P1, #115). One image, one answer, or refuse.
      // The sharer's OWN material index travels with it. Filtering first and
      // then reading the callback's index named an anonymous material by its
      // position in the FILTERED list — so material `#1`, arriving as the 0th
      // sharer, was called `#0`, matched no flatten request, and was counted as
      // dissenting: a consistent derive refused for a name it invented
      // (Codex P2, #115).
      const sharers = materials
        .map((other, index) => ({ other, index }))
        .filter(({ other, index }) => {
          if (index === m) return false;
          const otherMr = other.pbrMetallicRoughness?.metallicRoughnessTexture?.index;
          return otherMr !== undefined && textureImage(json, otherMr) === mrImage;
        });
      const dissenting = sharers.filter(({ other, index }) => {
        const otherName = other.name ?? `#${index}`;
        return !flattenRequests.some((r) => r.material === otherName && r.channel === request.channel);
      });
      if (dissenting.length > 0) {
        issues.push(
          `material "${name}" detaches ${request.channel}, but metallicRoughness image ${mrImage} is shared with ` +
            `${dissenting.length} material(s) that keep the map — flattening it would silently flatten theirs too; ` +
            `refused (§7)`,
        );
        continue;
      }
      const byte = Math.round(Math.min(1, Math.max(0, request.value)) * 255);
      const slot = request.channel === 'roughness' ? 'flattenG' : 'flattenB';
      const held = f[slot];
      if (held !== null && held !== byte) {
        issues.push(
          `materials sharing metallicRoughness image ${mrImage} want different ${request.channel} constants ` +
            `(${held} vs ${byte}) — one image cannot carry both; refused (§7)`,
        );
        continue;
      }
      f[slot] = byte;
    }
    const roughness = pbr?.roughnessFactor ?? 1;
    if (roughness > 1) {
      if (mrImage === null) {
        issues.push(
          `material "${name}" carries roughnessFactor ${roughness} (>1) but has no metallicRoughness ` +
            `texture to bake it into — a spec-clean file cannot carry it; refused (§7)`,
        );
      } else {
        const f = factsFor(mrImage);
        if (f.bakeFactor !== null && f.bakeFactor !== roughness) {
          issues.push(
            `materials sharing metallicRoughness image ${mrImage} want different roughness bakes ` +
              `(${f.bakeFactor} vs ${roughness}) — one image cannot carry both; refused (§7)`,
          );
        }
        f.bakeFactor = roughness;
      }
    } else if (mrImage !== null) {
      const f = factsFor(mrImage);
      if (f.bakeFactor !== null && f.bakeFactor !== 1) {
        // A ≤1 factor rides the factor slot; sharing an image with a >1 bake
        // would multiply this material's roughness too. Refuse the ambiguity.
        issues.push(
          `metallicRoughness image ${mrImage} is shared by a material baking roughness ×${f.bakeFactor} ` +
            `and material "${name}" keeping factor ${roughness} — the bake would corrupt it; refused (§7)`,
        );
      }
    }
  });

  // sRGB/linear conflicts refuse — one image cannot be filtered both ways.
  for (const f of facts.values()) {
    const srgb = SRGB_ROLES.some((r) => f.roles.has(r));
    const linear = LINEAR_ROLES.some((r) => f.roles.has(r));
    if (srgb && linear) {
      issues.push(
        `image ${f.index} serves both an sRGB role and a linear role (${[...f.roles].join(', ')}) — ` +
          `no single filter is honest for it; refused (§7)`,
      );
    }
  }
  if (issues.length > 0) throw new TextureMasterError(issues);

  // ---- what engages? ----
  const skipped: { index: number; reason: string }[] = [];
  const dims = new Map<number, { width: number; height: number }>();
  images.forEach((image, i) => {
    if (image.bufferView === undefined) {
      skipped.push({ index: i, reason: 'uri-referenced image (not embedded) — left untouched' });
      return;
    }
    // PNG and JPEG both derive (JPEG since 2026-09-01: Tripo's newer exports
    // ship JPEG, and a JPEG map meant the ceiling did not bite AND a >1
    // roughness bake had nowhere to land — it refused the whole export).
    const bytes = imageBytes(container, i);
    const d = pngDimensions(bytes) ?? jpegDimensions(bytes);
    if (d === null) {
      skipped.push({ index: i, reason: `${image.mimeType ?? 'unknown mime'} image — neither PNG nor JPEG; left untouched` });
      return;
    }
    dims.set(i, d);
  });
  const resizeEngaged = [...dims.values()].some((d) => Math.max(d.width, d.height) >= masterClassMinDim);
  const toProcess = new Set<number>();
  if (resizeEngaged) {
    for (const [i, d] of dims) {
      if (Math.max(d.width, d.height) > maxSize) toProcess.add(i);
    }
  }
  for (const f of facts.values()) {
    if (f.bakeFactor !== null && f.bakeFactor > 1) {
      if (!dims.has(f.index)) {
        throw new TextureMasterError([
          `material roughness ×${f.bakeFactor} must bake into image ${f.index}, which is not an embedded PNG — refused (§7)`,
        ]);
      }
      toProcess.add(f.index);
    }
    // A DETACHED channel engages the same way a bake does, and for the same
    // reason: the constant has to reach the plane, whatever size the file is.
    // Without this a delivery-class character would export with the map the
    // viewport was told to ignore.
    if (f.flattenG !== null || f.flattenB !== null) {
      if (!dims.has(f.index)) {
        throw new TextureMasterError([
          `a detached channel must be written into image ${f.index}, which is not an embedded PNG — refused (§7)`,
        ]);
      }
      toProcess.add(f.index);
    }
  }
  if (toProcess.size === 0) {
    return {
      glb,
      report: { changed: false, resizeEngaged, images: [], bakedRoughness: [], skipped },
    };
  }

  // ---- derive each engaged image by its role's rule ----
  const replacements = new Map<number, ImageReplacement>();
  const derived: DerivedImage[] = [];
  for (const index of [...toProcess].sort((a, b) => a - b)) {
    const f = factsFor(index);
    const raw = imageBytes(container, index);
    // One decoded shape for both encodings. jpeg-js always hands back 8-bit
    // RGBA; `useTArray` keeps it a Uint8Array rather than a Node Buffer, so
    // this stays a browser-safe path.
    const png = pngDimensions(raw) !== null
      ? decode(raw)
      : (() => {
          // jpeg-js caps decode memory at 512 MB by default, which an 8192²
          // master (268 MB of RGBA plus working space) trips — measured on
          // ratMan/ratFutureSoldier. The cap exists to bound a hostile file, so
          // it is raised deliberately rather than removed: 1 GB covers the 8K
          // master class with headroom and still refuses a decompression bomb.
          const j = decodeJpeg(raw, { useTArray: true, maxMemoryUsageInMB: 1024 });
          return { width: j.width, height: j.height, data: j.data, channels: 4, depth: 8 as const, palette: undefined };
        })();
    if (png.depth !== 8) {
      throw new TextureMasterError([`image ${index} is ${png.depth}-bit — this derive speaks 8-bit images only (§7)`]);
    }
    if (png.palette !== undefined) {
      throw new TextureMasterError([`image ${index} is palette-encoded — this derive speaks truecolor PNG only (§7)`]);
    }
    const { width, height, channels } = png;
    const data = png.data as Uint8Array;
    // The ceiling this image answers to: the LARGEST of its roles' ceilings
    // (see roleMaxSize), falling back to the flat maxSize for roles with no
    // entry. An unclassified image takes the flat ceiling.
    const roleCaps = [...f.roles].map((r) => roleMaxSize[r]).filter((c): c is number => c !== undefined);
    const imageMax = roleCaps.length > 0 ? Math.max(...roleCaps) : maxSize;
    const cap = resizeEngaged ? imageMax : Math.max(width, height);
    const scale = Math.min(1, cap / Math.max(width, height));
    const dw = Math.max(1, Math.round(width * scale));
    const dh = Math.max(1, Math.round(height * scale));

    const planes = toPlanes(data, channels);
    const resize = (p: Float32Array): Float32Array => resizePlane(p, width, height, dw, dh);
    const isSrgb = SRGB_ROLES.some((r) => f.roles.has(r));
    const isMr = f.roles.has('mr');
    const isNormal = f.roles.has('normal');
    let out: Float32Array[];
    let rule: string;
    if (isSrgb) {
      // Color channels filter in LINEAR space; alpha (coverage) filters plain.
      const colorCount = channels >= 3 ? 3 : 1;
      out = planes.map((p, c) =>
        c < colorCount ? linearPlaneToSrgbBytes(resize(srgbPlaneToLinear(p))) : roundPlane(resize(p)),
      );
      rule = 'sRGB-linear Lanczos';
    } else if (isMr) {
      if (channels < 3) {
        throw new TextureMasterError([
          `image ${index} serves metallicRoughness with ${channels} channel(s) — G=roughness/B=metallic need 3; refused (§7)`,
        ]);
      }
      const factor = f.bakeFactor !== null && f.bakeFactor > 1 ? f.bakeFactor : 1;
      out = planes.map((p, c) => {
        const r = resize(p);
        // A DETACHED channel is a constant, and it wins over both the map and
        // the bake — the point of detaching is that the map is not obeyed.
        if (c === 1 && f.flattenG !== null) return fillPlane(r, f.flattenG);
        if (c === 2 && f.flattenB !== null) return fillPlane(r, f.flattenB);
        if (c === 1) return factor !== 1 ? scalePlane(r, factor) : roundPlane(r); // G: roughness (+bake)
        if (c === 2) return rethresholdMetallic(r); // B: metal is binary
        return roundPlane(r); // R (occlusion or constant) + alpha: plain
      });
      const flattened = [f.flattenG !== null ? 'roughness' : null, f.flattenB !== null ? 'metallic' : null].filter(Boolean);
      rule =
        flattened.length > 0
          ? `MR (${flattened.join(' + ')} flattened to a constant)`
          : factor !== 1
            ? `MR (roughness ×${factor} baked, metallic re-thresholded)`
            : 'MR (metallic re-thresholded)';
    } else if (isNormal) {
      if (channels < 3) {
        throw new TextureMasterError([
          `image ${index} serves as a normal map with ${channels} channel(s) — a vector needs 3; refused (§7)`,
        ]);
      }
      const resized = planes.map((p) => resize(p));
      const n = renormalizeNormals(resized[0] as Float32Array, resized[1] as Float32Array, resized[2] as Float32Array);
      out = resized.map((p, c) => (c === 0 ? n.x : c === 1 ? n.y : c === 2 ? n.z : roundPlane(p)));
      rule = 'normal (renormalized)';
    } else {
      out = planes.map((p) => roundPlane(resize(p)));
      rule = 'plain Lanczos';
    }
    // Always written back as PNG: the derive's output is lossless by
    // construction, and a JPEG source re-encoded as JPEG would add a second
    // generation of loss to the very maps World is about to compress again.
    // The mime type travels with the bytes so the JSON never lies about them.
    const wasPng = pngDimensions(raw) !== null;
    replacements.set(index, {
      bytes: encode({ width: dw, height: dh, data: fromPlanes(out), channels, depth: 8 }),
      ...(wasPng ? {} : { mimeType: 'image/png' }),
    });
    derived.push({
      index,
      roles: [...f.roles],
      from: [width, height],
      to: [dw, dh],
      rule,
    });
  }

  // ---- bake accounting: baked factors leave the file as 1.0 ----
  const bakedImages = new Set([...facts.values()].filter((f) => f.bakeFactor !== null && f.bakeFactor > 1).map((f) => f.index));
  const bakedRoughness: { material: string; factor: number }[] = [];
  const nextMaterials = materials.map((material, m) => {
    const pbr = material.pbrMetallicRoughness;
    const mrTexture = pbr?.metallicRoughnessTexture?.index;
    const mrImage = mrTexture === undefined ? null : textureImage(json, mrTexture);
    const factor = pbr?.roughnessFactor ?? 1;
    // A flattened channel's value now lives in the texture, so its FACTOR must
    // become 1 or the constant would be multiplied by it on the way in.
    const f = mrImage === null ? null : facts.get(mrImage);
    const flatG = f?.flattenG ?? null;
    const flatB = f?.flattenB ?? null;
    // A flattened channel's value now lives in the texture, and so does a baked
    // roughness — BOTH resets have to happen, and they are not exclusive
    // (Codex P1, #115: detaching metallic while roughness carries a >1 bake
    // used to return here and leave the out-of-range factor in the file,
    // multiplying a plane that had already been scaled).
    const baked = mrImage !== null && bakedImages.has(mrImage) && factor > 1;
    if (flatG !== null || flatB !== null || baked) {
      const next: Record<string, unknown> = { ...pbr };
      if (flatG !== null || baked) next['roughnessFactor'] = 1;
      if (flatB !== null) next['metallicFactor'] = 1;
      if (baked) bakedRoughness.push({ material: material.name ?? `#${m}`, factor });
      return { ...material, pbrMetallicRoughness: next };
    }
    return material;
  });

  const next = replaceImages({ json: { ...json, materials: nextMaterials }, bin: container.bin }, replacements);
  return {
    glb: buildGlb(next.json, next.bin),
    report: { changed: true, resizeEngaged, images: derived, bakedRoughness, skipped },
  };
}
